/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\index.ts
 * 统一导出所有API服务
 */

// HTTP客户端
export { httpClient, TokenManager, HttpClient } from './http'
import { TokenManager as SharedTokenManager } from './http'

// 基础API服务类
export { BaseApiService } from './base'

// 认证服务
export { AuthService } from './auth'

// 企业管理服务
export { enterpriseApi } from './enterprises'
export type { 
  Enterprise as EnterpriseApiEntity,
  EnterpriseFilters, 
  EnterpriseFormData 
} from './enterprises'

// 企业工作日历
export { enterpriseCalendarApi } from './enterpriseCalendar'
export type { EnterpriseCalendarDTO, EnterpriseCalendarUpsertPayload } from './enterpriseCalendar'

// 硬件服务
export { hardwareApi } from './hardware'
export type { CabinetPool, CabinetPolicyTemplate } from './hardware'

// 商户管理服务
export { merchantApi } from './merchants'
export type { 
  Merchant, 
  MerchantFilters, 
  MerchantFormData,
  MerchantStatsData,
  MerchantBrand as MerchantBrandEntity,
  Cuisine as CuisineEntity,
  MerchantRestaurant,
  PaginatedMerchantResponse,
  MerchantTableColumnConfig
} from './merchants'

// 员工管理服务
export { employeeApi } from './employees'
export type {
  Employee,
  EmployeeFilters,
  EmployeeFormData,
  EmployeeStatsData,
  PaginatedEmployeeResponse,
  BatchResetPasswordResponse,
  BatchResetPasswordResult,
  BatchResetPasswordRequestOptions,
  BatchResetPasswordTaskStatus,
} from './employees'

// 企业×商户关系（职场×餐厅）
export { emsApi } from './enterprise_merchant_services'
export type { EmsRelation, EmsQuery, ConsumptionTypeCode } from './enterprise_merchant_services'

// 菜品库服务
export { dishApi } from './dishes'
export type { DishItem, DishFilters, PaginatedDishResponse } from './dishes'

// 菜单标签服务
export { menuTagsApi, PRESET_TAG_COLORS, tagColorUtils } from './menuTags'
export type {
  MenuTagListParams,
  MenuTagListResponse
} from './menuTags'

// 菜品标签服务
export { dishTagsApi } from './dishTags'
export type {
  DishTag,
  DishTagRequest,
  DishTagListParams,
  DishTagListResponse
} from './dishTags'

// 菜单服务
export { menusApi, MenuStatus, MENU_STATUS_CONFIG, menuUtils } from './menus'
export type {
  MenuListParams,
  MenuListResponse,
  MenuCreateData
} from './menus'

// 菜单排期服务
export { menuScheduleApi } from './menuSchedules'
export type { 
  MenuSchedule, 
  MenuItem, 
  MenuScheduleRequest, 
  MenuScheduleFilters,
  PaginatedMenuScheduleResponse,
  MenuDetail
} from './menuSchedules'

// 重新导出类型
export type * from '../types'

// ========== OpenAPI SDK 初始化（自动注入 BASE/TOKEN/Headers） ==========
// 说明：为使用 frontend/shared/generated 下生成的 SDK 提供默认配置。
// - BASE：来自 VITE_API_BASE_URL，默认 /api/v1
// - TOKEN：从 TokenManager 读取并注入 Authorization Bearer
// - HEADERS：注入 X-Operator-Id（如存在）
import { OpenAPI } from '../generated'
// 说明：生成的 SDK 路径已自带 "/api/v1/..." 前缀，此处 BASE 置空，避免拼出 /api/v1/api/v1/...
OpenAPI.BASE = ''
OpenAPI.TOKEN = async () => {
  try { return SharedTokenManager.getAccessToken() || '' } catch { return '' }
}
OpenAPI.HEADERS = async () => {
  try {
    const opId = localStorage.getItem('activeOperatorId')
    return opId ? { 'X-Operator-Id': String(opId) } : {}
  } catch { return {} as Record<string, string> }
}

// ========== 常量/字典 统一入口 ==========
export { constantsApi } from './constants'

// ========== 常量目录管理服务（CRUD） ==========
export { 
  catalogsManagementApi,
  merchantBrandsApi,
  cuisinesApi,
  dishCategoriesApi,
  serviceTypesApi,
  mealSegmentsApi,
  capabilitiesApi,
  // 新增的 API 实例
  enterpriseTypesApi,
  enterpriseIndustriesApi,
  orderStatusesApi,
  deliveryStatusesApi,
  complaintCategoriesApi,
  complaintTypesApi,
  // 只读常量函数
  getSpicinessLevels,
  getEmployeeRoleTags,
  getAdminDivisions,
  getHolidaysByYear,
  // 配置
  CATALOG_GROUPS,
  getCatalogConfigByKey,
  getWritableCatalogs
} from './catalogsManagement'
export type {
  CatalogItemBase,
  MerchantBrand,
  Cuisine,
  DishCategory,
  ServiceType,
  MealSegment,
  Capability,
  EnterpriseType,
  EnterpriseIndustry,
  OrderStatus,
  DeliveryStatus,
  ComplaintCategory,
  ComplaintType,
  EmployeeRoleTag,
  SpicinessLevel,
  AdminDivision,
  Holiday,
  CatalogItemInput,
  DishCategoryInput,
  ServiceTypeInput,
  MealSegmentInput,
  ComplaintTypeInput,
  CatalogQueryParams,
  PaginatedResponse,
  CatalogGroup,
  CatalogConfig
} from './catalogsManagement'

// ========== 职场管理服务 ==========
export { getEnterpriseWorkplaces, getWorkplacesCalendarStatus, getEnterpriseWorkplaceTree, getWorkplaceConfig, getWorkplaceFullConfig } from './workplaces'
export type { 
  Workplace, 
  WorkplaceListItem,
  EnterpriseWorkplaceTreeNode,
  WorkplaceConfig,
  WorkplaceFullConfig,
  MealSegmentConfigDetail,
  MealTimeRuleDetail
} from './workplaces'
export { getWorkplaceCalendarAvailability } from './workplaceCalendarAvailability'
export type {
  WorkplaceCalendarAvailabilityDate,
  WorkplaceCalendarAvailabilityItem,
  WorkplaceCalendarAvailabilityParams,
  WorkplaceCalendarAvailabilityResponse,
  WorkplaceCalendarSource,
} from './workplaceCalendarAvailability'

// ========== 租户（美膳）组织与人员服务 ==========
export {
  tenantDepartmentsApi,
  tenantStaffApi,
  operatorPositionsApi,
  operatorEmployeePositionsApi,
  listOperatorCities,
  getOperatorCity
} from './tenants'
export type { 
  TenantDepartment,
  TenantStaff,
  OperatorPosition,
  OperatorEmployeePosition,
  OperatorCity
} from './tenants'

// ========== 配送任务服务（Phase 8）==========
export { deliveryTasksApi } from './deliveryTasks'
export type {
  DeliveryTask,
  DeliveryTaskListParams,
  AssignDeliveryPersonRequest,
  UploadProofRequest,
  HandleLateRequest,
} from '../types/delivery'
export {
  DELIVERY_TASK_STATUS_MAP,
  DELIVERY_TASK_STATUS_TAG_TYPE,
} from '../types/delivery'
export type { DeliveryTaskStatus } from '../types/delivery'

// ========== 公告管理服务 ==========
export { announcementsApi } from './announcements'
export {
  AnnouncementType,
  AnnouncementStatus,
  TargetAudience,
  ANNOUNCEMENT_TYPE_CONFIG,
  ANNOUNCEMENT_STATUS_CONFIG,
  TARGET_AUDIENCE_CONFIG,
} from './announcements'
export type {
  AnnouncementListItem,
  AnnouncementDetail,
  AnnouncementPopupItem,
  AnnouncementFormData,
  AnnouncementListParams,
  AnnouncementPublishParams,
  AnnouncementScheduleParams,
  PaginatedAnnouncementResponse,
  UnreadCountResponse as AnnouncementUnreadCountResponse,
} from './announcements'

// 风控中心
export {
  listFraudAlerts,
  getFraudAlert,
  updateFraudAlertStatus,
  listDeviceLoginLogs,
  exportSecurityEmployees,
  EMPLOYEE_EXPORT_FILTER_OPTIONS
} from './security'
export type {
  FraudAlertListItem,
  FraudAlertDetail,
  DeviceLoginLogItem,
  FraudAlertListParams,
  DeviceLoginLogListParams,
  EmployeeExportFilter
} from './security'

// ========== 补贴转赠服务 ==========
export {
  getTransferConfig,
  getTransferableUsers,
  getTransferStatistics,
  listTransfers,
  getTransferDetail,
  createTransfer,
  cancelTransfer,
  getStatusTagType as getTransferStatusTagType,
  getStatusLabel as getTransferStatusLabel
} from './subsidyTransfer'
export type {
  TransferableUser,
  TransferConfig,
  TransferStatistics,
  SubsidyTransfer,
  TransferListParams,
  PaginatedTransferResponse,
  CreateTransferRequest
} from './subsidyTransfer'
